import { useCallback, useEffect, useState } from 'react';
import { apiCall, default as API_CONFIG } from '../config/api';
import {
  currentSubscription, deviceLabel, disablePush, enablePush, notificationPermission, pushSupport,
  saveSubscription,
} from '../notifications/push';

/**
 * State and actions for the Settings -> Push Notifications card.
 *
 * "Is this device on?" is answered by matching the browser's own
 * subscription endpoint against the backend's device list -- a browser
 * subscription the backend doesn't know about receives nothing, so it
 * counts as off.
 *
 * Returned shape:
 *   { support, permission, config, devices, thisDevice, loading, busy, error,
 *     testResult, enable, disable, removeDevice, sendTest }
 */
export const usePushNotifications = () => {
  const [support] = useState(pushSupport);
  const [permission, setPermission] = useState(notificationPermission);
  const [config, setConfig] = useState(null);
  const [devices, setDevices] = useState([]);
  const [endpoint, setEndpoint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const [cfg, list, sub] = await Promise.all([
        apiCall(API_CONFIG.endpoints.pushConfig),
        apiCall(API_CONFIG.endpoints.pushSubscriptions),
        currentSubscription().catch(() => null),
      ]);
      setConfig(cfg);
      setDevices(list || []);
      setEndpoint(sub?.endpoint || null);
      setError(null);

      // Relabel this device's row when the label logic has improved since it
      // subscribed (e.g. "(installed app)"). Only touches a row the backend
      // already has, so a device removed elsewhere is never re-added here.
      const mine = sub && (list || []).find((d) => d.endpoint === sub.endpoint);
      const label = deviceLabel();
      if (mine && mine.label !== label) {
        saveSubscription(sub)
          .then(() => setDevices((prev) => prev.map((d) => (d.id === mine.id ? { ...d, label } : d))))
          .catch((err) => console.warn('Could not update device label', err));
      }
    } catch (err) {
      console.error('Failed to load push notification state', err);
      setError('Could not load push notification status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const thisDevice = endpoint ? devices.find((d) => d.endpoint === endpoint) || null : null;

  // Wraps an action: one at a time, errors surfaced, list reloaded after.
  // Resolves to the action's result, or undefined if it failed.
  const run = async (action) => {
    setBusy(true);
    setError(null);
    setTestResult(null);
    try {
      return await action();
    } catch (err) {
      console.error('Push notification action failed', err);
      setError(err?.message || 'Something went wrong.');
      return undefined;
    } finally {
      setPermission(notificationPermission());
      await load();
      setBusy(false);
    }
  };

  // No await before enablePush: it must call requestPermission() inside the
  // click's user gesture or Safari drops the prompt.
  const enable = () => run(() => enablePush(config.publicKey));

  const disable = () => run(async () => {
    await disablePush();
    if (thisDevice) {
      await apiCall(API_CONFIG.endpoints.pushSubscriptionById(thisDevice.id), { method: 'DELETE' });
    }
  });

  const removeDevice = (id) => run(async () => {
    if (thisDevice?.id === id) await disablePush();
    await apiCall(API_CONFIG.endpoints.pushSubscriptionById(id), { method: 'DELETE' });
  });

  const sendTest = async () => {
    const result = await run(() => apiCall(API_CONFIG.endpoints.pushTest, { method: 'POST' }));
    if (result) setTestResult(result);
  };

  return {
    support, permission, config, devices, thisDevice, loading, busy, error,
    testResult, enable, disable, removeDevice, sendTest,
  };
};
